import { useState } from 'react';

interface UseIntegrationTestOptions {
    onTest: () => Promise<void>;
    successMessage: string;
    errorMessage?: string;
}

interface TestNowButtonProps {
    className?: string;
}

interface TestResultAlertProps {
    className?: string;
}

/**
 * Hook for "Test Now" UX on integration pages.
 * Owns the loading/result state and exposes a pair of components
 * so the button and the result alert can live in different parts of the DOM.
 */
export function useIntegrationTest({ onTest, successMessage, errorMessage = 'Test failed.' }: UseIntegrationTestOptions) {
    const [loading, setLoading] = useState<boolean>(false);
    const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

    const run = async () => {
        setLoading(true);
        setResult(null);
        try {
            await onTest();
            setResult({ success: true, message: successMessage });
        } catch (error: any) {
            const message = error?.response?.data || error?.message || errorMessage;
            setResult({ success: false, message });
        } finally {
            setLoading(false);
        }
    };

    const TestNowButton = ({ className = 'btn btn-info' }: TestNowButtonProps) => (
        <button type="button" className={className} onClick={run} disabled={loading}>
            <i className={`fal fa-fw ${loading ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}/>
            <span>{loading ? 'Testing...' : 'Test Now'}</span>
        </button>
    );

    const TestResultAlert = ({ className = '' }: TestResultAlertProps) => result ? (
        <div className={`${className} alert ${result.success ? 'alert-success' : 'alert-danger'} alert-dismissible fade show mt-2`} role="alert">
            {result.message}
            <button type="button" className="btn-close" onClick={() => setResult(null)} aria-label="Close"/>
        </div>
    ) : null;

    return { TestNowButton, TestResultAlert, loading, result };
}

export default useIntegrationTest;
